// src/pages/Dashboard/Admin/Parametres/components/UploadOrphansMaintenanceCard.jsx

import React, { useMemo, useState } from "react";
import {
  FaBroom,
  FaCopy,
  FaExclamationTriangle,
  FaFileAlt,
  FaFolderOpen,
  FaImage,
  FaRecycle,
  FaSearch,
  FaSyncAlt,
  FaTrashAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  cleanupEmptyUploadFolders,
  getUploadsOrphansReport,
  moveUploadOrphansToTrash,
  toAbsoluteUploadUrl,
} from "../../../../../../services/uploadMaintenanceServices";

import UploadTrashMaintenanceCard from "./UploadTrashMaintenanceCard";

const isImage = (extension = "") =>
  [".jpg", ".jpeg", ".png", ".webp", ".gif", ".svg"].includes(
    String(extension).toLowerCase(),
  );

const isPdf = (extension = "") => String(extension).toLowerCase() === ".pdf";

const formatBytes = (bytes) => {
  const value = Number(bytes || 0);

  if (value < 1024) return `${value} o`;

  if (value < 1024 * 1024) {
    return `${(value / 1024).toLocaleString("fr-FR", {
      maximumFractionDigits: 1,
    })} Ko`;
  }

  return `${(value / 1024 / 1024).toLocaleString("fr-FR", {
    maximumFractionDigits: 2,
  })} Mo`;
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function UploadOrphansMaintenanceCard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [folderFilter, setFolderFilter] = useState("");

  const [selectedPaths, setSelectedPaths] = useState(new Set());
  const [movingToTrash, setMovingToTrash] = useState(false);
  const [trashDrawerOpen, setTrashDrawerOpen] = useState(false);
  const [cleaningFolders, setCleaningFolders] = useState(false);

  const orphanFiles = report?.orphanFiles || [];

  const folders = useMemo(() => {
    return Array.from(new Set(orphanFiles.map((file) => file.folder))).sort(
      (a, b) => a.localeCompare(b, "fr"),
    );
  }, [orphanFiles]);

  const filteredFiles = useMemo(() => {
    const q = query.trim().toLowerCase();

    return orphanFiles.filter((file) => {
      const matchFolder = !folderFilter || file.folder === folderFilter;

      const searchable = [file.path, file.filename, file.folder, file.extension]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchQuery = !q || searchable.includes(q);

      return matchFolder && matchQuery;
    });
  }, [orphanFiles, query, folderFilter]);

  const totalSize = useMemo(() => {
    return filteredFiles.reduce((sum, file) => sum + Number(file.size || 0), 0);
  }, [filteredFiles]);

  const loadReport = async ({ silent = false } = {}) => {
    try {
      setLoading(true);

      const data = await getUploadsOrphansReport();
      setReport(data);
      setSelectedPaths(new Set());

      if (!silent) {
        toast.success("Analyse des fichiers terminée.");
      }
    } catch (error) {
      console.error("[UploadOrphansMaintenanceCard] load error:", error);

      toast.error(
        error?.message ||
          "Impossible d’analyser les fichiers présents dans /uploads.",
      );
    } finally {
      setLoading(false);
    }
  };

  const copyPath = async (path) => {
    try {
      await navigator.clipboard.writeText(path);
      toast.success("Chemin copié.");
    } catch {
      toast.error("Impossible de copier le chemin.");
    }
  };

  const toggleSelectedPath = (path) => {
    setSelectedPaths((prev) => {
      const next = new Set(prev);

      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }

      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedPaths(new Set(filteredFiles.map((file) => file.path)));
  };

  const clearSelection = () => {
    setSelectedPaths(new Set());
  };

  const movePathsToTrash = async (paths) => {
    if (!paths.length) {
      toast.info("Aucun fichier sélectionné.");
      return;
    }

    const confirmed = window.confirm(
      `Déplacer ${paths.length} fichier(s) orphelin(s) en corbeille ?`,
    );

    if (!confirmed) return;

    try {
      setMovingToTrash(true);

      const result = await moveUploadOrphansToTrash(paths);

      const movedCount = result?.summary?.moved || 0;
      const skippedCount = result?.summary?.skipped || 0;
      const errorCount = result?.summary?.errors || 0;

      if (movedCount > 0) {
        toast.success(`${movedCount} fichier(s) déplacé(s) en corbeille.`);
      }

      if (skippedCount > 0) {
        toast.warning(
          `${skippedCount} fichier(s) ignoré(s), probablement protégés.`,
        );
      }

      if (errorCount > 0) {
        toast.error(`${errorCount} erreur(s) pendant le déplacement.`);
      }

      await loadReport({ silent: true });
    } catch (error) {
      console.error("[UploadOrphansMaintenanceCard] move trash error:", error);

      toast.error(
        error?.message || "Impossible de déplacer les fichiers en corbeille.",
      );
    } finally {
      setMovingToTrash(false);
    }
  };

  const moveSelectedToTrash = () => {
    movePathsToTrash(Array.from(selectedPaths));
  };

  const moveSingleToTrash = (path) => {
    movePathsToTrash([path]);
  };

  const cleanupEmptyFolders = async () => {
    const confirmed = window.confirm(
      "Nettoyer les dossiers vides dans /uploads ?\n\nAucun fichier ne sera supprimé.",
    );

    if (!confirmed) return;

    try {
      setCleaningFolders(true);

      const result = await cleanupEmptyUploadFolders();
      const removed = result?.summary?.removed || 0;
      const errors = result?.summary?.errors || 0;

      if (removed > 0) {
        toast.success(`${removed} dossier(s) vide(s) supprimé(s).`);
      } else {
        toast.info("Aucun dossier vide à supprimer.");
      }

      if (errors > 0) {
        toast.error(`${errors} erreur(s) pendant le nettoyage.`);
      }

      await loadReport({ silent: true });
    } catch (error) {
      console.error(
        "[UploadOrphansMaintenanceCard] cleanup folders error:",
        error,
      );

      toast.error(
        error?.message || "Impossible de nettoyer les dossiers vides.",
      );
    } finally {
      setCleaningFolders(false);
    }
  };

  return (
    <>
      <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500" />

        <div className="p-4 sm:p-5 lg:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                Maintenance fichiers
              </p>

              <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
                <FaBroom className="text-orange-500" />
                Fichiers orphelins dans /uploads
              </h2>

              <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                Cette analyse affiche uniquement les fichiers présents sur le
                serveur mais non référencés dans les tables déclarées. Les
                fichiers peuvent être déplacés en corbeille avant suppression
                définitive.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setTrashDrawerOpen(true)}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-bold text-rose-700 transition hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20"
              >
                <FaRecycle />
                Corbeille
              </button>

              <button
                type="button"
                onClick={() => loadReport()}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
              >
                <FaSyncAlt className={loading ? "animate-spin" : ""} />
                {loading ? "Analyse..." : "Lancer l’analyse"}
              </button>

              <button
                type="button"
                onClick={cleanupEmptyFolders}
                disabled={cleaningFolders || loading || movingToTrash}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaBroom className={cleaningFolders ? "animate-pulse" : ""} />
                {cleaningFolders ? "Nettoyage..." : "Nettoyer dossiers vides"}
              </button>
            </div>
          </div>

          {report ? (
            <>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <StatCard
                  label="Fichiers scannés"
                  value={report.counts?.scannedFiles || 0}
                  icon={<FaFolderOpen />}
                />

                <StatCard
                  label="Références en base"
                  value={report.counts?.referencedPaths || 0}
                  icon={<FaFileAlt />}
                />

                <StatCard
                  label="Fichiers protégés"
                  value={report.counts?.protectedFiles || 0}
                  icon={<FaImage />}
                />

                <StatCard
                  label="Orphelins"
                  value={report.counts?.orphanFiles || 0}
                  icon={<FaExclamationTriangle />}
                  tone="amber"
                />

                <StatCard
                  label="Taille affichée"
                  value={formatBytes(totalSize)}
                  icon={<FaBroom />}
                  tone="slate"
                />
              </div>

              {report.warnings?.length ? (
                <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
                  <p className="font-bold">
                    {report.warnings.length} alerte(s) pendant l’analyse
                  </p>

                  <ul className="mt-2 space-y-1">
                    {report.warnings.slice(0, 5).map((warning, index) => (
                      <li key={index}>
                        {warning.label} — {warning.message}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="mt-5 grid gap-3 lg:grid-cols-[1fr_280px]">
                <div className="relative">
                  <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Rechercher un fichier, dossier, extension..."
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-semibold text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <select
                  value={folderFilter}
                  onChange={(event) => setFolderFilter(event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="">Tous les dossiers</option>

                  {folders.map((folder) => (
                    <option key={folder} value={folder}>
                      {folder.replace("/uploads/", "")}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-4 rounded-2xl bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-600 dark:bg-slate-950 dark:text-slate-300">
                {filteredFiles.length} fichier(s) affiché(s) sur{" "}
                {orphanFiles.length} orphelin(s).
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={selectAllFiltered}
                  disabled={!filteredFiles.length || movingToTrash}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Sélectionner les fichiers affichés
                </button>

                <button
                  type="button"
                  onClick={clearSelection}
                  disabled={!selectedPaths.size || movingToTrash}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Désélectionner
                </button>

                <button
                  type="button"
                  onClick={moveSelectedToTrash}
                  disabled={!selectedPaths.size || movingToTrash}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaTrashAlt
                    className={movingToTrash ? "animate-pulse" : ""}
                  />
                  {movingToTrash
                    ? "Déplacement..."
                    : `Déplacer en corbeille (${selectedPaths.size})`}
                </button>
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {filteredFiles.map((file) => (
                  <FileCard
                    key={file.path}
                    file={file}
                    onCopy={copyPath}
                    selected={selectedPaths.has(file.path)}
                    onToggleSelected={toggleSelectedPath}
                    onMoveToTrash={moveSingleToTrash}
                    disabled={movingToTrash}
                  />
                ))}
              </div>

              {!filteredFiles.length ? (
                <div className="mt-5 rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-10 text-center dark:border-slate-700 dark:bg-slate-950">
                  <p className="font-bold text-slate-900 dark:text-white">
                    Aucun fichier à afficher
                  </p>

                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Modifiez les filtres ou relancez l’analyse.
                  </p>
                </div>
              ) : null}
            </>
          ) : (
            <div className="mt-5 rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-10 text-center dark:border-slate-700 dark:bg-slate-950">
              <FaBroom className="mx-auto text-3xl text-slate-400" />

              <p className="mt-4 font-bold text-slate-900 dark:text-white">
                Aucune analyse lancée
              </p>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Lancez l’analyse pour voir les fichiers potentiellement
                supprimables.
              </p>
            </div>
          )}
        </div>
      </section>

      <UploadTrashMaintenanceCard
        open={trashDrawerOpen}
        onClose={() => setTrashDrawerOpen(false)}
        onTrashEmptied={() => {
          loadReport({ silent: true });
        }}
      />
    </>
  );
}

function StatCard({ label, value, icon, tone = "indigo" }) {
  const tones = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    slate:
      "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-300",
  };

  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/80 text-lg dark:bg-slate-950/60">
          {icon}
        </div>

        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] opacity-70">
            {label}
          </p>

          <p className="mt-0.5 break-words text-lg font-bold leading-tight text-slate-950 dark:text-white">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

function FileCard({
  file,
  onCopy,
  selected,
  onToggleSelected,
  onMoveToTrash,
  disabled,
}) {
  const url = toAbsoluteUploadUrl(file.path);

  return (
    <article className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="flex h-44 items-center justify-center bg-slate-100 dark:bg-slate-900">
        {isImage(file.extension) ? (
          <img
            src={url}
            alt={file.filename}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : isPdf(file.extension) ? (
          <div className="flex flex-col items-center gap-2 text-rose-600 dark:text-rose-300">
            <FaFileAlt className="text-4xl" />
            <span className="text-sm font-bold">PDF</span>
          </div>
        ) : (
          <FaFileAlt className="text-4xl text-slate-400" />
        )}
      </div>

      <div className="space-y-3 p-4">
        <div className="flex items-center justify-between gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggleSelected(file.path)}
              disabled={disabled}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            Sélectionner
          </label>

          <button
            type="button"
            onClick={() => onMoveToTrash(file.path)}
            disabled={disabled}
            className="inline-flex items-center gap-1 rounded-xl bg-rose-50 px-2 py-1 text-xs font-bold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20"
            title="Déplacer ce fichier en corbeille"
          >
            <FaTrashAlt />
            Corbeille
          </button>
        </div>

        <div>
          <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
            {file.filename}
          </p>

          <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
            {file.folder}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 text-[11px] font-bold">
          <span className="rounded-full bg-amber-50 px-2.5 py-1 text-amber-700 ring-1 ring-amber-100 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20">
            ORPHELIN
          </span>

          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {formatBytes(file.size)}
          </span>

          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {file.extension}
          </span>
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400">
          Modifié le {formatDate(file.modifiedAt)}
        </p>

        <div className="flex gap-2">
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex flex-1 items-center justify-center rounded-xl bg-slate-950 px-3 py-2 text-xs font-bold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            Voir
          </a>

          <button
            type="button"
            onClick={() => onCopy(file.path)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            title="Copier le chemin"
          >
            <FaCopy />
          </button>
        </div>
      </div>
    </article>
  );
}
